import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ChartBase from './ChartBase';
import { resolveChartData, getItemValue, getItemLabel, getMaxValue } from './ChartData';
import '../../styles/chart-styles.css';

const PieChart = ({ 
  data, 
  moduleId, 
  dataIndex, 
  title,
  subtitle,
  showLabels = true, 
  animate = true,
  showLegend = true,
  donut = false,
  className = '',
  ...props 
}) => {
  const { getModuleData } = useTheme();
  
  // Get the entire module data array
  const moduleData = getModuleData(moduleId);
  const chartArray = resolveChartData(data, moduleData);
  const maxValue = getMaxValue(chartArray);

  return (
    <ChartBase title={title} subtitle={subtitle}>
      <PieChartRenderer 
        chartArray={chartArray}
      />
    </ChartBase>
  );
};

// Internal PieChart rendering logic
const PieChartRenderer = ({ chartArray }) => {
  // Use actual data from chartArray instead of hardcoded values
  const pieData = chartArray.map((item, index) => {
    const value = getItemValue(item);
    const label = getItemLabel(item, index);
    
    // Use theme colors with fallbacks
    const themeColors = ['var(--theme-primary)', 'var(--theme-secondary)', 'var(--theme-bg)'];
    const color = typeof item === 'object' && item.color 
      ? item.color 
      : themeColors[index % themeColors.length];
    
    return { label, value, color };
  });
  
  const total = pieData.reduce((sum, item) => sum + item.value, 0);
  
  // Handle case where all values are 0
  if (total === 0) {
    return (
      <div className="chart--pie w-full h-64 theme-bg rounded-lg shadow-md p-4 flex items-center justify-center">
        <div className="theme-primary opacity-70 text-center">
          <div className="text-lg mb-2">📊</div>
          <div>No data to display</div>
        </div>
      </div>
    );
  }
  
  let currentAngle = 0;
  const paths = pieData.map((segment) => {
    const percentage = (segment.value / total) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    
    const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
    const y1 = 40 + 40 * Math.sin((startAngle * Math.PI) / 180);
    const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
    const y2 = 40 + 40 * Math.sin((endAngle * Math.PI) / 180);
    
    const largeArcFlag = angle > 180 ? 1 : 0;
    
    const path = `M 50 40 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
    
    currentAngle = endAngle;
    
    return (
      <g key={segment.label}>
        <path d={path} fill={segment.color} />
      </g>
    );
  });

  return (
    <div className="chart--pie w-full h-64 theme-bg rounded-lg shadow-md p-4">
      {/* Chart Content */}
      <div className="flex h-full">
        {/* Pie chart container */}
        <div className="flex-1 flex items-center justify-center">
          <svg viewBox="0 0 100 100" className="w-full h-full max-w-xs">
            {paths}
          </svg>
        </div>
        
        {/* Legend container */}
        <div className="flex-shrink-0 ml-4 flex flex-col justify-center space-y-2">
          {pieData.map((segment) => (
            <div key={segment.label} className="flex items-center text-xs">
              <div 
                className="w-3 h-3 rounded mr-2 flex-shrink-0" 
                style={{ backgroundColor: segment.color }}
              />
              <span className="theme-primary whitespace-nowrap">
                {segment.label}: {segment.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PieChart;
